import useI18n from '@/shared/hooks/usei18n'
import type { ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import {
  GoodsFamily,
  Tooltip,
  Checkbox,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  DataBlock
} from '@/common'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Importer } from '@/features/importers/hooks/importers'
import { getImportedGoodEligibility } from '@/shared/utils/importedGoodEligibility'
import { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import { Installation } from '@/features/suppliers/hooks/installations'
import ReactCountryFlag from 'react-country-flag'
import ImportedGoodActionsMenu from './ImportedGoodActionsMenu'
import StatusCell from './StatusCell'
import { ListRow } from './ImportedGoodsList'
import cx from 'classnames'
import CnCodeTag from '@/shared/components/CnCodeTag'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'
const formatValue = (value: string | undefined | null) => value || '-'

export function ListRowCell({
  children,
  className,
  title
}: {
  children: React.ReactNode
  className?: string
  title?: string
}) {
  return (
    <div className={cx('p-2', className)} title={title}>
      {children}
    </div>
  )
}

export default function ImportedGoodListRow({
  layout,
  gridTemplateColumns,
  importedGood,
  isRepresentative,
  onEdit,
  onView,
  isEditable,
  onDelete,
  liabilityData,
  installation,
  importer,
  supplier,
  isSelected,
  onToggleSelect,
  showCheckbox
}: {
  layout: 'table' | 'card'
  gridTemplateColumns: string
  importedGood: ImportedGoodExtended
  isRepresentative: boolean
  installation: Installation | null
  importer: Importer | null
  supplier: Partnership | null
  liabilityData: BatchLiabilityResultItem | null
  onEdit: (
    importedGood: ImportedGoodExtended,
    supplierName: string,
    installationName: string,
    goodsFamily?: GoodsFamily
  ) => void
  onView?: (importedGood: ImportedGoodExtended) => void
  isEditable: boolean
  onDelete: (importedGood: ImportedGoodExtended) => void
  isSelected?: boolean
  onToggleSelect?: (id: string) => void
  showCheckbox?: boolean
}) {
  const { t, formatCbamTco2Display } = useI18n()
  const { formatNetMass, netMassColumnLabel } = useNetMassDisplay()

  const eligibility = getImportedGoodEligibility(importedGood, isRepresentative)

  const projection2026 = liabilityData?.result?.projection?.[2026]
  const netEmissions = projection2026?.netEmissions ?? undefined
  const totalEmissions = projection2026?.totalEmissions ?? undefined
  const freeAllocationTCo2 = projection2026?.freeAllocationTCo2 ?? undefined

  // Tooltip content for Total Certificates Required
  const certificatesTooltipContent =
    netEmissions !== undefined ? (
      <div className="space-y-2">
        <div className="font-semibold">
          {t('cbamReporting:cbamReportDetails.reportedGoods.certificatesTooltip.formula')}
        </div>
        <div className="space-y-1 text-sm">
          <div>
            {formatCbamTco2Display(totalEmissions)} − {formatCbamTco2Display(freeAllocationTCo2)}
          </div>
        </div>
      </div>
    ) : null

  const handleEdit = () => {
    onEdit(importedGood, supplier?.name || '', installation?.name || '', installation?.goodsFamily)
  }

  const handleDelete = () => {
    onDelete(importedGood)
  }

  const handleView = () => {
    onView?.(importedGood)
  }

  // Prepare producer data with flag and supplier
  const producerData = (
    <div>
      <div className="flex items-center gap-1.5">
        {installation?.country && (
          <ReactCountryFlag countryCode={installation.country} className="shrink-0" svg />
        )}
        <span>{formatValue(installation?.name)}</span>
      </div>
      {supplier?.name && (
        <p className="text-sm text-text-secondary mt-1">{formatValue(supplier?.name)}</p>
      )}
    </div>
  )

  if (layout === 'table') {
    return (
      <ListRow gridTemplateColumns={gridTemplateColumns}>
        {showCheckbox && (
          <div className="flex items-center justify-center p-2">
            <Checkbox
              id={`select-${importedGood.id}`}
              checked={isSelected ?? false}
              className="inline-block"
              onChange={() => onToggleSelect?.(importedGood.id)}
              disabled={!isEditable}
            />
          </div>
        )}

        <div className="flex items-center justify-center p-2">
          <StatusCell eligibility={eligibility} />
        </div>
        <div className="p-2 truncate space-y-0.5">
          <CnCodeTag cnCode={importedGood.cnCode} />
          {importedGood.description && (
            <p
              title={importedGood.description}
              className="text-sm text-text-secondary text-ellipsis overflow-hidden block"
            >
              {formatValue(importedGood.description)}
            </p>
          )}
        </div>
        <div className="p-2 flex items-center gap-1.5 min-w-0">
          {importedGood.country ? (
            <>
              <ReactCountryFlag countryCode={importedGood.country} className="shrink-0" svg />
              <span className="truncate">
                {t(`common:countriesByIsoAlpha2.${importedGood.country}`)}
              </span>
            </>
          ) : (
            <span className="text-text-secondary">-</span>
          )}
        </div>
        <div className="min-w-0 p-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="truncate">{formatValue(installation?.name)}</p>
          </div>
          <p className="text-sm text-text-secondary truncate">{formatValue(supplier?.name)}</p>
        </div>
        {isRepresentative && <div className="truncate p-2">{formatValue(importer?.name)}</div>}
        <div className="truncate p-2">{formatValue(importedGood.sellerName)}</div>
        <div className="text-right p-2">
          {formatNetMass(importedGood.netMassInTonnes)}
        </div>
        <div className="p-2">
          {importedGood.isUsingDefaultEmissions
            ? t('cbamReporting:cbamReportDetails.reportedGoods.table.emissionsTypeDefault')
            : t('cbamReporting:cbamReportDetails.reportedGoods.table.emissionsTypeActual')}
        </div>
        <div className="text-right p-2">
          {certificatesTooltipContent ? (
            <Tooltip content={certificatesTooltipContent} position="top">
              <div className="cursor-help">{formatCbamTco2Display(netEmissions)}</div>
            </Tooltip>
          ) : (
            formatCbamTco2Display(netEmissions)
          )}
        </div>
        <div className="flex gap-2 justify-end p-2">
          <ImportedGoodActionsMenu
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isDisabled={!isEditable}
          />
        </div>
      </ListRow>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {showCheckbox && (
            <Checkbox
              id={`select-card-${importedGood.id}`}
              checked={isSelected ?? false}
              className="inline-block"
              onChange={() => onToggleSelect?.(importedGood.id)}
              disabled={!isEditable}
            />
          )}
          <div className="flex-1 min-w-0">
            <CardTitle title={importedGood.cnCode} />
            {importedGood.description && (
              <p className="text-sm text-text-secondary mt-1">
                {formatValue(importedGood.description)}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <StatusCell eligibility={eligibility} />
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DataBlock
              label={t(
                'cbamReporting:cbamReportDetails.reportedGoods.table.headers.countryOfOrigin'
              )}
              data={
                importedGood.country ? (
                  <div className="flex items-center gap-1.5">
                    <ReactCountryFlag countryCode={importedGood.country} className="shrink-0" svg />
                    <span>{t(`common:countriesByIsoAlpha2.${importedGood.country}`)}</span>
                  </div>
                ) : (
                  '-'
                )
              }
            />
            <DataBlock
              label={t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.producer')}
              data={producerData}
            />
            {isRepresentative && (
              <DataBlock
                label={t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.importer')}
                data={formatValue(importer?.name)}
              />
            )}
            <DataBlock
              label={t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.seller')}
              data={formatValue(importedGood.sellerName)}
            />
            <DataBlock label={netMassColumnLabel} data={formatNetMass(importedGood.netMassInTonnes)} />
            <DataBlock
              label={t('cbamReporting:cbamReportDetails.reportedGoods.table.headers.emissionsType')}
              data={
                importedGood.isUsingDefaultEmissions
                  ? t('cbamReporting:cbamReportDetails.reportedGoods.table.emissionsTypeDefault')
                  : t('cbamReporting:cbamReportDetails.reportedGoods.table.emissionsTypeActual')
              }
            />
            <DataBlock
              label={t(
                'cbamReporting:cbamReportDetails.reportedGoods.table.headers.totalCertificatesRequired'
              )}
              data={
                certificatesTooltipContent ? (
                  <Tooltip content={certificatesTooltipContent} position="top">
                    <div className="cursor-help">{formatCbamTco2Display(netEmissions)}</div>
                  </Tooltip>
                ) : (
                  formatCbamTco2Display(netEmissions)
                )
              }
              tooltip={
                certificatesTooltipContent
                  ? t('cbamReporting:cbamReportDetails.reportedGoods.certificatesTooltip.formula')
                  : undefined
              }
            />
          </div>

          <div className="flex justify-end pt-2">
            <ImportedGoodActionsMenu
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDisabled={!isEditable}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
